<script setup>
import { computed } from 'vue';
import { Copy01Icon } from '@hugeicons/core-free-icons';
import 'highlight.js/styles/default.css';
import 'highlight.js/lib/common';
import NextButton from 'dashboard/components-next/button/Button.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  script: {
    type: String,
    default: '',
  },
  lang: {
    type: String,
    default: 'javascript',
  },
  enableCodePen: {
    type: Boolean,
    default: false,
  },
  codepenTitle: {
    type: String,
    default: 'Chatwoot Codepen',
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const scrubbedScript = computed(() => {
  // remove trailing and leading extra lines and not spaces
  const scrubbed = props.script.replace(/^\s*[\r\n]/gm, '');
  const lines = scrubbed.split('\n');

  // remove extra indentations
  const minIndent = lines.reduce((min, line) => {
    if (line.trim().length === 0) return min;
    const indent = line.match(/^\s*/)[0].length;
    return Math.min(min, indent);
  }, Infinity);

  return lines.map(line => line.slice(minIndent)).join('\n');
});

const displayCode = computed(() => {
  const code = scrubbedScript.value;
  if (!props.compact) return code;

  return code
    .replace(/(-->)\s*(<script\b)/gi, '$1\n$2')
    .replace(/(<\/script>)\s*(<!--)/gi, '$1\n$2')
    .trim();
});

const codepenScriptValue = computed(() => {
  const lang = props.lang === 'javascript' ? 'js' : props.lang;
  return JSON.stringify({
    title: props.codepenTitle,
    private: true,
    [lang]: scrubbedScript.value,
  });
});

const onCopy = async e => {
  e.preventDefault();
  await copyTextToClipboard(displayCode.value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div class="relative w-full min-w-0 text-left">
    <div
      class="top-1.5 absolute ltr:right-1.5 rtl:left-1.5 flex backdrop-blur-sm rounded-lg items-center gap-1"
    >
      <form
        v-if="enableCodePen"
        class="flex items-center"
        action="https://codepen.io/pen/define"
        method="POST"
        target="_blank"
      >
        <input type="hidden" name="data" :value="codepenScriptValue" />
        <NextButton
          slate
          xs
          type="submit"
          faded
          :label="t('COMPONENTS.CODE.CODEPEN')"
        />
      </form>
      <NextButton
        v-tooltip.top="t('COMPONENTS.CODE.BUTTON_TEXT')"
        icon-only
        slate
        xs
        faded
        :title="t('COMPONENTS.CODE.BUTTON_TEXT')"
        @click="onCopy"
      >
        <template #icon>
          <HugeIcon :icon="Copy01Icon" :size="16" class="shrink-0" />
        </template>
      </NextButton>
    </div>
    <highlightjs
      v-if="script"
      :language="lang"
      :code="displayCode"
      class="[&_code]:text-start"
      :class="[
        compact &&
          '[&_pre]:overflow-x-hidden [&_.hljs]:overflow-x-hidden [&_.hljs]:max-w-full [&_.hljs]:whitespace-pre-wrap [&_.hljs]:break-all [&_code]:whitespace-pre-wrap [&_code]:break-all [&_.hljs]:p-3 [&_.hljs]:text-[11px] [&_.hljs]:leading-relaxed',
      ]"
    />
  </div>
</template>
